// MuseumSettings.jsx - Component for managing museum configuration
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, X, Building, MapPin, List, Settings, Link, FileText } from 'lucide-react';

const MuseumSettings = ({
  museum,
  onUpdateMuseum,
  onAddRoom,
  onRemoveRoom,
  onUpdateRooms,
  onAddCategory,
  onRemoveCategory,
  onUpdateCategories,
  onLinkGuide,
  onUnlinkGuide,
  availableGuides = [], // List of guides from database for selection
  isLoading = false
}) => {
  const [activeSection, setActiveSection] = useState('info');
  const [editingMuseum, setEditingMuseum] = useState({
    name: '',
    description: '',
    address: '',
    website: '',
    phone: ''
  });

  // Room management state
  const [newRoom, setNewRoom] = useState({ name: '', floor: 1 });
  const [rooms, setRooms] = useState([]);

  // Category management state
  const [newCategory, setNewCategory] = useState('');
  const [categories, setCategories] = useState([]);

  // Linked guides state
  const [linkedGuideIds, setLinkedGuideIds] = useState([]);
  const [newGuideId, setNewGuideId] = useState('');

  // Update local state when museum changes
  useEffect(() => {
    if (museum) {
      setEditingMuseum({
        name: museum.name || '',
        description: museum.description || '',
        address: museum.address || '',
        website: museum.website || '',
        phone: museum.phone || ''
      });
      setRooms(museum.rooms || []);
      setCategories(museum.categories || []);
      setLinkedGuideIds(museum.linkedGuideIds || []);
    }
  }, [museum]);

  // Handle museum info update
  const handleSaveMuseumInfo = async () => {
    if (!museum?.id) return;

    try {
      await onUpdateMuseum(museum.id, editingMuseum);
    } catch (err) {
      console.error('Failed to update museum info:', err);
    }
  };

  // Room management handlers
  const handleAddRoom = async () => {
    if (!newRoom.name.trim() || !museum?.id) return;

    try {
      const roomWithId = {
        id: `room-${Date.now()}`,
        name: newRoom.name.trim(),
        floor: parseInt(newRoom.floor) || 1
      };

      await onAddRoom(museum.id, roomWithId);
      setNewRoom({ name: '', floor: 1 });
    } catch (err) {
      console.error('Failed to add room:', err);
    }
  };

  const handleRemoveRoom = async (room) => {
    if (!museum?.id) return;
    if (!window.confirm(`Remove room "${room.name}"? Objects in this room will not be deleted.`)) {
      return;
    }

    try {
      await onRemoveRoom(museum.id, room);
    } catch (err) {
      console.error('Failed to remove room:', err);
    }
  };

  const handleMoveRoom = async (index, direction) => {
    const newRooms = [...rooms];
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= newRooms.length) return;

    [newRooms[index], newRooms[newIndex]] = [newRooms[newIndex], newRooms[index]];
    setRooms(newRooms);

    try {
      await onUpdateRooms(museum.id, newRooms);
    } catch (err) {
      console.error('Failed to reorder rooms:', err);
    }
  };

  // Category management handlers
  const handleAddCategory = async () => {
    if (!newCategory.trim() || !museum?.id) return;

    const categoryLower = newCategory.trim().toLowerCase();
    if (categories.includes(categoryLower)) {
      alert('This category already exists');
      return;
    }

    try {
      await onAddCategory(museum.id, categoryLower);
      setNewCategory('');
    } catch (err) {
      console.error('Failed to add category:', err);
    }
  };

  const handleRemoveCategory = async (category) => {
    if (!museum?.id) return;
    if (!window.confirm(`Remove category "${category}"? Objects with this category will not be affected.`)) {
      return;
    }

    try {
      await onRemoveCategory(museum.id, category);
    } catch (err) {
      console.error('Failed to remove category:', err);
    }
  };

  // Guide linking handlers
  const handleLinkGuide = async () => {
    if (!newGuideId.trim() || !museum?.id || !onLinkGuide) return;

    const guideId = newGuideId.trim();
    if (linkedGuideIds.includes(guideId)) {
      alert('This guide is already linked');
      return;
    }

    try {
      await onLinkGuide(museum.id, guideId);
      setNewGuideId('');
    } catch (err) {
      console.error('Failed to link guide:', err);
    }
  };

  const handleUnlinkGuide = async (guideId) => {
    if (!museum?.id || !onUnlinkGuide) return;
    if (!window.confirm(`Unlink this guide from the museum? The guide will no longer have access to this museum's objects.`)) {
      return;
    }

    try {
      await onUnlinkGuide(museum.id, guideId);
    } catch (err) {
      console.error('Failed to unlink guide:', err);
    }
  };

  // Get guide info from available guides list
  const getGuideInfo = (guideId) => {
    const guide = availableGuides.find(g =>
      g.ID?.toString() === guideId ||
      g.id?.toString() === guideId
    );
    return guide;
  };

  if (!museum) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Select a museum to manage its settings</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-800">Museum Settings</h2>
        </div>
        <p className="text-gray-600 mt-1 text-sm">Configure museum information, rooms, and categories</p>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveSection('info')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeSection === 'info'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            Museum Info
          </div>
        </button>
        <button
          onClick={() => setActiveSection('rooms')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeSection === 'rooms'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Rooms ({rooms.length})
          </div>
        </button>
        <button
          onClick={() => setActiveSection('categories')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeSection === 'categories'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <List className="w-4 h-4" />
            Categories ({categories.length})
          </div>
        </button>
        <button
          onClick={() => setActiveSection('guides')}
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeSection === 'guides'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Link className="w-4 h-4" />
            Linked Guides ({linkedGuideIds.length})
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Museum Info Section */}
        {activeSection === 'info' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Museum Name *
              </label>
              <input
                type="text"
                value={editingMuseum.name}
                onChange={(e) => setEditingMuseum(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Museu Nacional de Arte Antiga"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={editingMuseum.description}
                onChange={(e) => setEditingMuseum(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of the museum..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={editingMuseum.address}
                onChange={(e) => setEditingMuseum(prev => ({ ...prev, address: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Museum address..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={editingMuseum.website}
                  onChange={(e) => setEditingMuseum(prev => ({ ...prev, website: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={editingMuseum.phone}
                  onChange={(e) => setEditingMuseum(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+351 ..."
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={handleSaveMuseumInfo}
                disabled={isLoading || !editingMuseum.name.trim()}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Rooms Section */}
        {activeSection === 'rooms' && (
          <div className="space-y-4">
            {/* Add Room Form */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-800 mb-3">Add New Room</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Room name (e.g., Sala 1 - Egyptian Art)"
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select
                  value={newRoom.floor}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, floor: parseInt(e.target.value) }))}
                  className="w-24 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[-1, 0, 1, 2, 3, 4, 5].map(floor => (
                    <option key={floor} value={floor}>
                      {floor === -1 ? 'B1' : floor === 0 ? 'GF' : `F${floor}`}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddRoom}
                  disabled={!newRoom.name.trim() || isLoading}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>

            {/* Rooms List */}
            <div className="space-y-2">
              {rooms.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No rooms defined yet. Add your first room above.
                </p>
              ) : (
                rooms.map((room, index) => (
                  <div
                    key={room.id}
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm"
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />

                    <div className="flex-1">
                      <span className="font-medium text-gray-800">{room.name}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({room.floor === -1 ? 'B1' : room.floor === 0 ? 'Ground Floor' : `Floor ${room.floor}`})
                      </span>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => handleMoveRoom(index, -1)}
                        disabled={index === 0}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                        title="Move up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveRoom(index, 1)}
                        disabled={index === rooms.length - 1}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                        title="Move down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemoveRoom(room)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title="Remove room"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Categories Section */}
        {activeSection === 'categories' && (
          <div className="space-y-4">
            {/* Add Category Form */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-800 mb-3">Add New Category</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Category name (e.g., sculpture, painting)"
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategory.trim() || isLoading}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>

            {/* Categories List */}
            <div className="flex flex-wrap gap-2">
              {categories.length === 0 ? (
                <p className="text-gray-500 text-center py-8 w-full">
                  No categories defined yet. Add your first category above.
                </p>
              ) : (
                categories.map((category) => (
                  <div
                    key={category}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-full"
                  >
                    <span className="text-blue-800 capitalize">{category}</span>
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="text-blue-400 hover:text-red-500"
                      title="Remove category"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Linked Guides Section */}
        {activeSection === 'guides' && (
          <div className="space-y-4">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">About Linked Guides</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Link audioguide JSON files to this museum. When a guide is linked, its JSON metadata
                    will include <code className="bg-blue-100 px-1 rounded">linkedMuseumId</code> pointing
                    to this museum's objects. Multiple guides can share the same objects (e.g., different
                    language versions or different tour routes).
                  </p>
                </div>
              </div>
            </div>

            {/* Add Guide Form */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-800 mb-3">Link a Guide</h3>
              <div className="flex gap-3">
                {availableGuides.length > 0 ? (
                  <select
                    value={newGuideId}
                    onChange={(e) => setNewGuideId(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a guide...</option>
                    {availableGuides
                      .filter(g => !linkedGuideIds.includes(g.ID?.toString()) && !linkedGuideIds.includes(g.id?.toString()))
                      .map(guide => (
                        <option key={guide.ID || guide.id} value={guide.ID || guide.id}>
                          {guide.Name || guide.name} ({guide.Language || guide.language || 'N/A'})
                        </option>
                      ))
                    }
                  </select>
                ) : (
                  <input
                    type="text"
                    value={newGuideId}
                    onChange={(e) => setNewGuideId(e.target.value)}
                    placeholder="Enter guide ID (e.g., 123 or guide_abc)"
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleLinkGuide()}
                  />
                )}
                <button
                  onClick={handleLinkGuide}
                  disabled={!newGuideId.trim() || isLoading || !onLinkGuide}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Link className="w-4 h-4" />
                  Link
                </button>
              </div>
              {!onLinkGuide && (
                <p className="text-amber-600 text-sm mt-2">
                  Guide linking is not available in this context.
                </p>
              )}
            </div>

            {/* Linked Guides List */}
            <div className="space-y-2">
              {linkedGuideIds.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No guides linked yet. Link a guide above to share this museum's objects.
                </p>
              ) : (
                linkedGuideIds.map((guideId) => {
                  const guideInfo = getGuideInfo(guideId);
                  return (
                    <div
                      key={guideId}
                      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm"
                    >
                      <FileText className="w-5 h-5 text-blue-500" />

                      <div className="flex-1">
                        {guideInfo ? (
                          <>
                            <span className="font-medium text-gray-800">
                              {guideInfo.Name || guideInfo.name}
                            </span>
                            <span className="ml-2 text-sm text-gray-500">
                              ({guideInfo.Language || guideInfo.language || 'N/A'})
                            </span>
                          </>
                        ) : (
                          <span className="font-medium text-gray-800">
                            Guide ID: {guideId}
                          </span>
                        )}
                      </div>

                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        ID: {guideId}
                      </span>

                      <button
                        onClick={() => handleUnlinkGuide(guideId)}
                        disabled={!onUnlinkGuide}
                        className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Unlink guide"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* JSON Metadata Note */}
            {linkedGuideIds.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-gray-700 mb-2">JSON Metadata</h4>
                <p className="text-sm text-gray-600 mb-2">
                  When exporting linked guides, include this in the guide's metadata:
                </p>
                <code className="block bg-gray-100 p-2 rounded text-sm text-gray-800">
                  "linkedMuseumId": "{museum?.id}"
                </code>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MuseumSettings;
